import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiCalendar, 
  FiClock, 
  FiUser, 
  FiMail, 
  FiBriefcase, 
  FiSend,
  FiCheckCircle,
  FiMonitor,
  FiZap,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiGlobe
} from 'react-icons/fi';
import axios from 'axios';
import { trackPageView, trackFormSubmission } from '../utils/analytics';
import './Demo.css';

const Demo = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    use_case: '',
    preferred_date: '',
    preferred_time: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/demo', {
      page_title: 'LogCrest Demo - See LogCrest in Action',
      page_category: 'demo'
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Track form submission
      trackFormSubmission('demo_request', {
        form_fields: Object.keys(formData),
        preferred_time: formData.preferred_time,
        company: formData.company
      });

      await axios.post('/api/demo/request', formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        role: '',
        use_case: '',
        preferred_date: '',
        preferred_time: ''
      });
    } catch (error) {
      console.error('Failed to submit demo request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <FiMonitor />,
      title: 'Real-time Monitoring',
      description: 'Monitor your logs and infrastructure in real-time with intelligent alerts and comprehensive dashboards.'
    },
    {
      icon: <FiShield />,
      title: 'Advanced Security',
      description: 'Protect your infrastructure with AI-powered security insights and compliance monitoring.'
    },
    {
      icon: <FiZap />,
      title: 'AI-Powered Insights',
      description: 'Get intelligent recommendations and automated responses to optimize your infrastructure.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Performance Analytics',
      description: 'Analyze performance trends and identify bottlenecks before they impact your users.'
    },
    {
      icon: <FiUsers />,
      title: 'Team Collaboration',
      description: 'Collaborate effectively with role-based access control and shared dashboards.'
    },
    {
      icon: <FiGlobe />,
      title: 'Multi-Cloud Support',
      description: 'Monitor across AWS, GCP, Azure, and on-premise environments seamlessly.'
    }
  ];

  const systemHealthMetrics = [
    {
      title: 'System Health',
      value: '98.7%',
      change: '+2.1%',
      icon: <FiMonitor />,
      description: 'Monitor your infrastructure health with real-time system status and automated alerts.'
    },
    {
      title: 'Active Users',
      value: '2,847',
      change: '+12.3%',
      icon: <FiUsers />,
      description: 'Track user engagement and system usage patterns with comprehensive analytics.'
    }
  ];

  const timeSlots = [
    '9:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM',
    '6:00 PM'
  ];

  return (
    <div className="demo">
      {/* Hero Section - Demo Form */}
      <section className="demo-hero">
        <div className="container">
          <div className="demo-hero-content">
            <motion.div
              className="form-info"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Schedule your demo</h1>
              <p>
                Fill out the form below and we'll get back to you within 24 hours 
                to schedule your personalized demo. No commitment required.
              </p>
              <div className="demo-benefits">
                <div className="benefit-item">
                  <FiCheckCircle />
                  <span>Personalized to your infrastructure</span>
                </div>
                <div className="benefit-item">
                  <FiCheckCircle />
                  <span>30-minute focused session</span>
                </div>
                <div className="benefit-item">
                  <FiCheckCircle />
                  <span>Q&A with our experts</span>
                </div>
                <div className="benefit-item">
                  <FiCheckCircle />
                  <span>Free trial setup assistance</span>
                </div>
              </div>
            </motion.div>

            <motion.form
              className="demo-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3>Demo Request Form</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <FiUser />
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <FiMail />
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">
                    <FiBriefcase />
                    Company *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="role" className="form-label">Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select your role</option>
                    <option value="DevOps Engineer">DevOps Engineer</option>
                    <option value="SRE">Site Reliability Engineer</option>
                    <option value="Platform Engineer">Platform Engineer</option>
                    <option value="CTO">CTO</option>
                    <option value="Engineering Manager">Engineering Manager</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferred_date" className="form-label">
                    <FiCalendar />
                    Preferred Date
                  </label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      id="preferred_date"
                      name="preferred_date"
                      value={formData.preferred_date}
                      onChange={handleInputChange}
                      className="form-input date-input"
                      min={new Date().toISOString().split('T')[0]}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="preferred_time" className="form-label">
                    <FiClock />
                    Preferred Time
                  </label>
                  <select
                    id="preferred_time"
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time} IST</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="use_case" className="form-label">Use Case</label>
                <textarea
                  id="use_case"
                  name="use_case"
                  value={formData.use_case}
                  onChange={handleInputChange}
                  className="form-input form-textarea"
                  placeholder="Tell us about your current infrastructure and monitoring challenges..."
                />
              </div>

              {submitStatus === 'success' && (
                <div className="form-success">
                  <FiCheckCircle />
                  Thank you! We'll contact you within 24 hours to schedule your demo.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-error">
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Scheduling...
                  </>
                ) : (
                  <>
                    Schedule Demo
                    <FiSend />
                  </>
                )}
              </button>
            </motion.form>
          </div>
        </div>
      </section>

      {/* Demo Features */}
      <section className="demo-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>What you'll see in the demo</h2>
            <div className="grid grid-3">
              {/* First feature card - Real-time Monitoring */}
              <motion.div
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0 * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="feature-icon">
                  {features[0].icon}
                </div>
                <h3>{features[0].title}</h3>
                <p>{features[0].description}</p>
              </motion.div>

              {/* 2 Metric cards */}
              {systemHealthMetrics.map((metric, index) => (
                <motion.div
                  key={`metric-${index}`}
                  className="health-metric-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: (index + 1) * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="metric-header">
                    <div className="metric-icon">
                      {metric.icon}
                    </div>
                    <h3>{metric.title}</h3>
                  </div>
                  <div className="metric-value">
                    <span className="value">{metric.value}</span>
                    {metric.change && (
                      <span className={`change ${metric.change.startsWith('+') ? 'positive' : 'negative'}`}>
                        {metric.change}
                      </span>
                    )}
                  </div>
                  <p className="metric-description">{metric.description}</p>
                </motion.div>
              ))}

              {/* Remaining feature cards */}
              {features.slice(1).map((feature, index) => (
                <motion.div
                  key={index + 1}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: (index + 3) * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default Demo;
